import classNames from 'classnames';
import { divIcon, LatLngBounds } from 'leaflet';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import ThemeContext from '../../../providers/ThemeProvider';

/**
 * Don't use this directly as ssr is not supported by react-leaflet,
 * use WowMap which dynamically imports the component client side only
 */
const MapComponent = ({ className, zoom, maxZoom, pins }) => {
  const [map, setMap] = useState(null);
  const theme = useContext(ThemeContext);

  useEffect(() => {
    if (map && pins && pins.length > 0) {
      // get bounds and add a little padding so pins aren't on edges of map
      const latLons = pins.map((pin) => [pin.lat, pin.lon]);
      const bounds = new LatLngBounds(latLons).pad(0.1);
      if (zoom) {
        // ignore maxZoom and set defined zoom
        map.setView(bounds.getCenter(), zoom);
      } else {
        // zoom to show all pins but don't zoom further than the max zoom
        const boundsZoom = map.getBoundsZoom(bounds);
        const _zoom = Math.min(boundsZoom, maxZoom);
        map.setView(bounds.getCenter(), _zoom);
      }
    }
  }, [pins, map, zoom, maxZoom]);

  return (
    <>
      <MapContainer
        className={classNames('wow-map', className)}
        scrollWheelZoom
        whenCreated={(_map) => setMap(_map)}
      >
        <TileLayer
          attribution='Maps &copy; <a href="https://www.thunderforest.com/">Thunderforest</a>, Data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.thunderforest.com/neighbourhood/{z}}/{x}/{y}.png?apikey=ded142062c184099b57f4a9ee090a0bf"
        />
        {pins.map((pin, index) => (
          <Marker
            icon={divIcon({ html: '<span class="wow-map__pin"></div>' })}
            key={index}
            position={[pin.lat, pin.lon]}
          >
            <Popup>
              <h3>{pin.title}</h3>
              {pin.body}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <style jsx>{`
        :global(.wow-map) {
          height: 320px;
          margin-bottom: 20px;
        }
        :global(.wow-map__pin) {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 28px;
          height: 28px;
          border-radius: 48px 48px 0;
          transform: rotate(45deg);
          transform-origin: 100% 100%;
          border: 1px solid #fff;
          box-shadow: 3px -2px 3px #888;
          background-color: ${theme.colors.primary};
        }
        :global(.leaflet-marker-icon) {
          height: 0 !important;
          width: 0 !important;
          background: none;
          border: none;
          margin: 0 !important;
        }
        :global(.leaflet-popup) {
          font-size: 11px;
          margin-bottom: 40px !important;
        }
        :global(.leaflet-popup p) {
          margin: 0;
        }
      `}</style>
    </>
  );
};

MapComponent.propTypes = {
  className: PropTypes.string,
  maxZoom: PropTypes.number,
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      body: PropTypes.node,
      lat: PropTypes.number,
      lon: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  zoom: PropTypes.number,
};

MapComponent.defaultProps = {
  className: '',
  maxZoom: 15,
  pins: [],
  zoom: undefined,
};

export default MapComponent;
